import React, { useState, useRef } from 'react';
import { Search, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { statusColor } from '../utils/helpers';

function dotColor(status) {
  const c = statusColor(status);
  if (c.includes('green')) return 'var(--c-green)';
  if (c.includes('red')) return 'var(--c-red)';
  if (c.includes('orange') || c.includes('amber') || c.includes('yellow')) return 'var(--c-amber)';
  return 'var(--gray-light)';
}

export function StatusBadge({ status }) {
  return (
    <span className={`status-badge ${statusColor(status)}`}>
      <span className="status-dot" style={{ background: dotColor(status) }} />
      {status}
    </span>
  );
}

export function SearchBar({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input value={value} onChange={e => onChange(e.target.value)}
        className="input-field pl-9 w-full md:w-64" placeholder={placeholder} />
    </div>
  );
}

// Optional multi-select: pass `selected` (Set of row.id), `onToggle(id)`,
// `onToggleAll(rows)` to render a checkbox column (Feature 3 bulk actions)
// onSelectRange(ids) — optional; when provided, shift-clicking a checkbox
// selects every row between it and the last one clicked (display order)
export function DataTable({ columns, data, onRowClick, emptyMessage = 'No records found.', selected, onToggle, onToggleAll, onSelectRange }) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const lastIdx = useRef(null);
  const selectable = !!(selected && onToggle);

  const sorted = sort.key ? [...data].sort((a, b) => {
    const va = a[sort.key] ?? ''; const vb = b[sort.key] ?? '';
    return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  }) : data;

  const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100">
            {selectable && (
              <th className="table-header w-8">
                <input type="checkbox" className="rounded"
                  checked={data.length > 0 && selected.size === data.length}
                  onChange={() => onToggleAll?.(data)} />
              </th>
            )}
            {columns.map(col => (
              <th key={col.key} className={`table-header cursor-pointer select-none ${col.className || ''}`}
                onClick={() => col.sortable !== false && toggleSort(col.key)}>
                <span className="flex items-center gap-1">
                  {col.label}
                  {sort.key === col.key && (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-10 text-gray-400">{emptyMessage}</td></tr>
          ) : sorted.map((row, i) => (
            <tr key={row.id ?? i} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${selectable && selected.has(row.id) ? 'bg-blue-50/40' : ''}`}
              onClick={() => onRowClick?.(row)}>
              {selectable && (
                <td className="table-cell w-8" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" className="rounded" checked={selected.has(row.id)} onChange={() => {}}
                    onMouseDown={e => e.shiftKey && e.preventDefault()}
                    onClick={e => {
                      if (e.shiftKey && onSelectRange && lastIdx.current !== null) {
                        const [lo, hi] = [Math.min(lastIdx.current, i), Math.max(lastIdx.current, i)];
                        onSelectRange(sorted.slice(lo, hi + 1).map(r => r.id));
                      } else onToggle(row.id);
                      lastIdx.current = i;
                    }} />
                </td>
              )}
              {columns.map(col => (
                <td key={col.key} className={`table-cell ${col.className || ''}`}>
                  {col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="page-title" style={{ color: 'var(--title-color, #1e3a5f)' }}>{title}</h1>
        {subtitle && <p className="page-subtitle text-gray-500 text-sm mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, size = 'md', isOpen, footer }) {
  if (isOpen === false) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`modal-panel bg-white rounded-2xl shadow-2xl w-full ${widths[size]} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
        {footer && <div className="px-5 pb-5 border-t pt-4">{footer}</div>}
      </div>
    </div>
  );
}

export function FormField({ label, required, children, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}
